// Role / label configuration — admin-managed defaults with per-district overrides.
//
// Goal: eliminate hardcoded role names, self-reported role options, and
// grade level lists scattered across the codebase, in favor of one shared
// source of truth that admins can edit through the Roles tab.
//
// Three categories are configurable:
//
//   1. systemRoleLabels — display names for system claims (admin / districtAdmin
//      / teacher / ea / tls). The claim KEYS stay code-controlled (they're
//      the permission boundary) — only the display labels are configurable.
//      Example override: Madison district might show "tls" as
//      "Reading Specialist" instead of the default "TLS (Teaching Learning
//      Specialist)".
//
//   2. professionalRoles — options in the "Self-Reported Role" dropdown
//      (Lead Teacher / Special Educator / etc.). A district can override
//      the entire list to fit their staffing terminology.
//
//   3. gradeLevels — options in the "Grade Level" dropdown.
//
// Storage:
//   /appConfig/roles                    — master defaults (one document)
//   /districts/{id}.roleOverrides       — sparse override on the district doc
//
// District overrides are sparse for systemRoleLabels (per-key override) and
// full-replacement for the lists. A missing override key = "use default."
//
// Resolution order:
//   district override -> appConfig default -> built-in fallback (this file)

import { collection, doc, getDoc, getDocs, setDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Built-in fallbacks. Used when /appConfig/roles doesn't exist yet (fresh
// install) or fails to load. Don't remove these — they're the safety net.
export const FALLBACK_SYSTEM_ROLE_LABELS = {
  admin:         'Global Admin',
  districtAdmin: 'District Admin',
  teacher:       'Teacher',
  ea:            'EA (Assistant)',
  tls:           'TLS (Teaching Learning Specialist)',
};

export const FALLBACK_PROFESSIONAL_ROLES = [
  'Lead Teacher',
  'Co-Teacher / Assistant',
  'Special Educator',
  'Principal / Admin',
  'Other',
];

export const FALLBACK_GRADE_LEVELS = [
  'Early Childhood',
  'Pre-K',
  'Kindergarten',
  '1st Grade',
  '2nd Grade',
  '3rd Grade',
  'Other',
];

// System role keys are code-bound. Surface them so the admin UI can
// enumerate the rows without hardcoding the list there too.
export const SYSTEM_ROLE_KEYS = Object.keys(FALLBACK_SYSTEM_ROLE_LABELS);

/**
 * Loads the master defaults. Returns the FALLBACK_* values if the doc
 * doesn't exist yet. Safe to call repeatedly — caller should cache.
 */
export async function loadRoleConfig() {
  try {
    const snap = await getDoc(doc(db, 'appConfig', 'roles'));
    if (!snap.exists()) {
      return {
        systemRoleLabels: { ...FALLBACK_SYSTEM_ROLE_LABELS },
        professionalRoles: [...FALLBACK_PROFESSIONAL_ROLES],
        gradeLevels: [...FALLBACK_GRADE_LEVELS],
      };
    }
    const data = snap.data() || {};
    return {
      systemRoleLabels: { ...FALLBACK_SYSTEM_ROLE_LABELS, ...(data.systemRoleLabels || {}) },
      professionalRoles: Array.isArray(data.professionalRoles) && data.professionalRoles.length
        ? data.professionalRoles : [...FALLBACK_PROFESSIONAL_ROLES],
      gradeLevels: Array.isArray(data.gradeLevels) && data.gradeLevels.length
        ? data.gradeLevels : [...FALLBACK_GRADE_LEVELS],
    };
  } catch (err) {
    console.error('roleConfig.loadRoleConfig failed, falling back to built-ins:', err);
    return {
      systemRoleLabels: { ...FALLBACK_SYSTEM_ROLE_LABELS },
      professionalRoles: [...FALLBACK_PROFESSIONAL_ROLES],
      gradeLevels: [...FALLBACK_GRADE_LEVELS],
    };
  }
}

/**
 * Saves the master defaults. Only callable by global admins (rules
 * enforce). Caller is responsible for showing UI-side feedback.
 */
export async function saveRoleConfig(updates, { uid } = {}) {
  await setDoc(
    doc(db, 'appConfig', 'roles'),
    { ...updates, updatedAt: serverTimestamp(), updatedBy: uid || null },
    { merge: true },
  );
}

/**
 * Reads district roleOverrides for a given districtId. Returns null if
 * district doesn't exist or has no overrides.
 */
export async function loadDistrictRoleOverrides(districtId) {
  if (!districtId) return null;
  try {
    const snap = await getDoc(doc(db, 'districts', districtId));
    if (!snap.exists()) return null;
    return snap.data().roleOverrides || null;
  } catch (err) {
    console.error('roleConfig.loadDistrictRoleOverrides failed:', err);
    return null;
  }
}

/**
 * Loads districts that DO have a roleOverrides field set. Used by the
 * admin RolesTab to show "districts with custom labels" in one place
 * without scanning every district.
 */
export async function loadDistrictsWithOverrides() {
  // No Firestore query for "field exists," so fall back to scanning all
  // active districts and filtering client-side. Schools registry already
  // does the same thing — districts per-tenant are bounded.
  const snap = await getDocs(query(collection(db, 'districts'), where('active', '==', true)));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => d.roleOverrides && Object.keys(d.roleOverrides).length > 0);
}

// ─── Resolvers ────────────────────────────────────────────────────────────────
// All resolvers take a baseConfig (from loadRoleConfig) and a districtOverrides
// blob (from loadDistrictRoleOverrides), and return the resolved value.
// Order: district override -> baseConfig -> FALLBACK_*.

export function resolveSystemRoleLabel(roleKey, baseConfig, districtOverrides) {
  return districtOverrides?.systemRoleLabels?.[roleKey]
      ?? baseConfig?.systemRoleLabels?.[roleKey]
      ?? FALLBACK_SYSTEM_ROLE_LABELS[roleKey]
      ?? roleKey;
}

export function resolveProfessionalRoles(baseConfig, districtOverrides) {
  if (Array.isArray(districtOverrides?.professionalRoles) && districtOverrides.professionalRoles.length) {
    return districtOverrides.professionalRoles;
  }
  if (Array.isArray(baseConfig?.professionalRoles) && baseConfig.professionalRoles.length) {
    return baseConfig.professionalRoles;
  }
  return FALLBACK_PROFESSIONAL_ROLES;
}

export function resolveGradeLevels(baseConfig, districtOverrides) {
  if (Array.isArray(districtOverrides?.gradeLevels) && districtOverrides.gradeLevels.length) {
    return districtOverrides.gradeLevels;
  }
  if (Array.isArray(baseConfig?.gradeLevels) && baseConfig.gradeLevels.length) {
    return baseConfig.gradeLevels;
  }
  return FALLBACK_GRADE_LEVELS;
}
